import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { Button } from 'devextreme-react/button';
import { Column, Pager, Paging, Selection } from 'devextreme-react/data-grid';
import { LoadIndicator } from 'devextreme-react/load-indicator';
import { formatMessage } from 'devextreme/localization';
import SplitterLayout from 'react-splitter-layout';
import PopupMVP from '../PopupMVP';
import HeaderButtonMVP from '../HeaderButtonMVP';
import DataGridMVP from '../DataGridMVP';
import columnDataGrid from './columnDataGrid';
import linesColumnDataGrid from './linesColumnDataGrid';
import { debounce } from '../../utils/functions';
import style from './so.module.scss';

const SalesOrders = ({
  plantId,
  soReducer: {
    listSO,
    soRP,
    defaultCheckedRPKeys,
    loadingRP,
    loadingSO,
    loadingRebind,
    error
  },
  modalErrorOpen,
  tabsReducer: { tabSelect },
  userReducer: { permissions },
  getSO,
  getSORP,
  rebindSORP
}) => {
  const [headButtonVisible, setHeadButtonVisible] = useState({ display: 'none' });
  const [selectedRPKeys, setSelectedRPKeys] = useState(defaultCheckedRPKeys);
  const [visibleModalBindUnbind, setVisibleModalBindUnbind] = useState(false);

  /** Выбор строк */
  const [dataSource, setDataSource] = useState({
    focusedRow: null,
    selectList: []
  });

  /** Фильтры по таблицам */
  const [filterDataGrid, setFilterDataGrid] = useState({
    dateFrom: '',
    dateTo: '',
    filter: null
  });

  const [thisDataGridInstance, setThisDataGridInstance] = useState({});

  /** Установка дефолтно выбранных рулонов */
  useEffect(() => {
    setSelectedRPKeys(defaultCheckedRPKeys);
    if (soRP.length && permissions.indexOf('prmChangeSO') !== -1) {
      setHeadButtonVisible({ display: 'flex' });
    } else {
      setHeadButtonVisible({ display: 'none' });
    }
    setVisibleModalBindUnbind(false);
  }, [defaultCheckedRPKeys, soRP, permissions]);

  /** Обновление данных при переключении на вкладку */
  useEffect(() => {
    if (plantId && tabSelect === 11) {
      getSO({
        ...filterDataGrid,
        plantId
      });
    }
  }, [getSO, filterDataGrid, tabSelect, plantId]);

  useEffect(() => {
    if (error) {
      setVisibleModalBindUnbind(false);
    }
  }, [error]);

  /** Событие на выделение строк позиций заказа */
  const onSOSelectionChanged = useCallback(
    debounce(({ selectedRowsData }) => {
      setDataSource((item) => ({
        ...item,
        selectList: selectedRowsData
      }));
      setHeadButtonVisible({ display: 'none' });
      if (selectedRowsData.length && selectedRowsData[0].salesOrderLineId) {
        getSORP({
          salesOrderLineId: selectedRowsData[0].salesOrderLineId
        });
      }
    }, 1500),
    [selectedRPKeys]
  );

  /** Событие на выделение строк готовой продукции */
  const onRPSelectionChanged = useCallback(
    (selectedRowKeys) => {
      const selectedItems = soRP.filter((item) => selectedRowKeys.includes(item.pieceNumId));
      const selectedWeight = selectedItems.reduce((acc, item) => acc + item.weight, 0);
      const salesOrderNum = dataSource.selectList.length && dataSource.selectList[0].salesOrderNum;
      const salesOrderStatus = dataSource.selectList.length && dataSource.selectList[0].statusName;
      const salesOrderLineNum = dataSource.selectList.length
      && dataSource.selectList[0].salesOrderLineNum;
      const salesOrderLineMaxCapacity = dataSource.selectList.length
      && dataSource.selectList[0].salesOrderLineMaxCapacity;
      const newlySelectedItems = soRP.filter((item) =>
        selectedRowKeys.includes(item.pieceNumId)
        && (item.checkbox === 'NOT_SELECTED' || !selectedRPKeys.includes(item.pieceNumId)));

      if (salesOrderStatus === 'Создана' && newlySelectedItems.length > 0) {
        setHeadButtonVisible({ display: 'none' });
        modalErrorOpen({
          message: `${formatMessage('textModal').textSOWrongStatusLeft} ${salesOrderNum}
          ${formatMessage('textModal').textSOWrongStatusCenter} ${salesOrderLineNum}
          ${formatMessage('textModal').textSOWrongStatusRight}`,
          techInfo: '',
          types: 'error',
          title: formatMessage('textError').warning
        });
        return;
      }
      if (selectedWeight > salesOrderLineMaxCapacity) {
        setHeadButtonVisible({ display: 'none' });
        modalErrorOpen({
          message: `${formatMessage('textModal').textIncomingProhibitionLeft}${salesOrderNum}
          ${formatMessage('textModal').textZdvlCloseP2}${salesOrderLineNum}
          ${formatMessage('textModal').textSOMaxToleranceWarn} 
          ${formatMessage('textModal').textSOMaxCapacity}${salesOrderLineMaxCapacity}
          ${formatMessage('textModal').textSOBinding}${selectedWeight.toFixed(3)}`,
          techInfo: '',
          types: 'error',
          title: formatMessage('textError').warning
        });
      } else if (permissions.indexOf('prmChangeSO') !== -1) {
        setHeadButtonVisible({ display: 'flex' });
      }

      setSelectedRPKeys(selectedRowKeys);
    },
    [modalErrorOpen, soRP, dataSource, selectedRPKeys, permissions]
  );

  /** Строка для подстановки */
  const textSO = useMemo(() => {
    const salesOrderId = dataSource.selectList.length
      && dataSource.selectList[0].salesOrderSystemNum;
    const salesOrderLineNum = dataSource.selectList.length
     && dataSource.selectList[0].salesOrderLineNum;
    const bind = [];
    const unbind = [];
    const selectedItems = soRP.filter((item) => selectedRPKeys.includes(item.pieceNumId));
    const unCheckedKeys = defaultCheckedRPKeys.filter((el) => !selectedRPKeys.includes(el));

    selectedItems.forEach((item) => {
      if (item.checkbox === 'NOT_SELECTED') {
        bind.push(item.qcId);
      }
    });
    soRP.forEach((item) => {
      if (unCheckedKeys.includes(item.pieceNumId)) {
        unbind.push(item.qcId);
      }
    });

    const getString = (array, b = false) => array.length
      ? `${formatMessage('textModal')[b ? 'textSOBindP1' : 'textSOBindP3']} ${array.join(', ')}
    ${formatMessage('textModal')[b ? 'textSOBindP2' : 'textSOBindP4']} ${salesOrderId}
    ${formatMessage('textModal').textSOBindP5} ${salesOrderLineNum}?`
      : '';

    return [getString(bind, true), getString(unbind)];
  }, [dataSource.selectList, soRP, defaultCheckedRPKeys, selectedRPKeys]);

  const modalOnHiding = useCallback(() => {
    setVisibleModalBindUnbind(false);
  }, []);

  const onClickBindUnbind = useCallback(() => {
    setVisibleModalBindUnbind(true);
  }, []);

  // Сброс выделенных строк при переключении фильтра
  const onFilterChanged = useCallback(() => {
    setHeadButtonVisible({ display: 'none' });
    thisDataGridInstance.instance.deselectAll();
    setDataSource({ ...dataSource, focusedRow: null, selectList: [] });
  }, [dataSource, thisDataGridInstance.instance]);

  /** Поисковая строка */
  const dataGridOnOptionChanged = useCallback(
    ({ value, name }) => {
      if (name === 'searchPanel') {
        setFilterDataGrid({ ...filterDataGrid, filter: value });
      }
    },
    [filterDataGrid]
  );

  /** Дизейбл кнопки "Привязать/Отвязать" если нет изменений в привязаной ГП */
  const disabledBindUnbindButton = useMemo(() =>
    selectedRPKeys.length === defaultCheckedRPKeys.length
    && !selectedRPKeys.filter((el) => !defaultCheckedRPKeys.includes(el)).length,
  [defaultCheckedRPKeys, selectedRPKeys]);

  /** Функция для привязки/отвязки ГП */
  const onClickYes = useCallback(() => {
    rebindSORP({
      needToBeUntied: true,
      pieceNumIds: [...selectedRPKeys],
      solNumId: dataSource.selectList.length && dataSource.selectList[0].salesOrderLineId,
      ...filterDataGrid,
      plantId
    });
  }, [dataSource, selectedRPKeys, rebindSORP, filterDataGrid, plantId]);

  /** Событие при смене фокуса с позиции заказа */
  const handleFocusChange = useCallback(
    ({ row }) => {
      if (row) {
        setDataSource((item) => ({
          ...item,
          focusedRow: {
            row: row.rowIndex,
            ...row.data
          }
        }));
      }
    }, []
  );

  return (
    <>
      <HeaderButtonMVP headButtVisibol={headButtonVisible}>
        <Button
          text={formatMessage('textButton').bind_unbind}
          type={'default'}
          disabled={disabledBindUnbindButton}
          stylingMode={'contained'}
          onClick={onClickBindUnbind}
        />
      </HeaderButtonMVP>

      <SplitterLayout>
        <div className={style.topSide}>
          <p className={style.title}>{formatMessage('textTabSO')}</p>
          <DataGridMVP
            dataSource={listSO}
            className={`${style.topTable} salesOrders`}
            keyExpr="salesOrderLineId"
            setDataGridInstance={setThisDataGridInstance}
            onFocusedRowChanged={handleFocusChange}
            onSelectionChanged={onSOSelectionChanged}
            filterDataGrid={filterDataGrid}
            onFilterChanged={onFilterChanged}
            setFilterDataGrid={setFilterDataGrid}
            setStateStoringName="dataGridSalesOrdersListUpd"
            dataGridOnOptionChanged={dataGridOnOptionChanged}
            tText={formatMessage('textPeriod')}
            isDataLoading={loadingSO}
            focusedRowEnabled
            focusedRowIndex={
              dataSource.focusedRow ? dataSource.focusedRow.row : -1
            }
            visibleButtonQRCode={false}
            visibleInfoIcon={false}
            visibleButtonDateRangePicker
            visibleActyalButton={false}
            visibleSummary={false}
            onClickUpdate={() => {}}
            scrollingMode="standard"
          >
            {columnDataGrid.map((item) => (
              <Column
                dataField={item.dataField}
                caption={formatMessage('textTableSO')[item.dataField]}
                visible={!item.notVisible}
                allowSearch
                allowFiltering
                key={item.dataField}
                dataType={item.dataType}
                cssClass={item.cssClass}
                width={item.width ? item.width : undefined}
                cellRender={item.cellRender}
              />
            ))}
            <Selection mode="single" showCheckBoxesMode="none" />
            <Paging defaultPageSize={20} />
            <Pager
              showPageSizeSelector
              allowedPageSizes={[20, 30, 100]} />
          </DataGridMVP>
        </div>
        <div className={style.bottomSide}>
          <p className={style.bottomTitle}>
            {formatMessage('textBindChangeProducts')}
          </p>
          <DataGridMVP
            dataSource={soRP}
            keyExpr="pieceNumId"
            className={style.bottomTable}
            selectedRowKeys={selectedRPKeys}
            tText={formatMessage('textPeriod')}
            setStateStoringName="dataGridSalesOrderRPList"
            isDataLoading={loadingRP}
            visibleButtonQRCode={false}
            visibleInfoIcon={false}
            visibleButtonDateRangePicker={false}
            visibleActyalButton={false}
            onCellClick={(e) => {
              if (e.rowType === 'header') {
                if (
                  e.cellElement.firstChild.getAttribute('aria-checked') === 'false'
                  || e.cellElement.firstChild.getAttribute('aria-checked') === 'mixed'
                ) {
                  onRPSelectionChanged([]);
                } else if (
                  e.cellElement.firstChild.getAttribute('aria-checked') === 'true'
                ) {
                  onRPSelectionChanged(soRP.map((el) => el.pieceNumId));
                }
              } else if (e.column && e.column.type === 'selection') {
                onRPSelectionChanged(e.component.getSelectedRowKeys());
              }
            }}
            visibleButtonCheckAll
            visibleSummary={false}
            outerDataForFooter={
              dataSource.selectList.length && dataSource.selectList[0].salesOrderLineMaxCapacity
            }
            onClickUpdate={() => {}}
            scrollingMode="standard"
          >
            {linesColumnDataGrid.map((item) => (
              <Column
                dataField={item.dataField}
                caption={formatMessage('textTableSO')[item.dataField]}
                visible={!item.notVisible}
                allowSearch
                allowFiltering
                key={item.dataField}
                dataType={item.dataType}
                cssClass={item.cssClass}
                width={item.width ? item.width : undefined}
              />
            ))}
            <Paging defaultPageSize={20} />
            <Pager
              showPageSizeSelector
              allowedPageSizes={[20, 30, 100]} />
          </DataGridMVP>
        </div>
      </SplitterLayout>
      <PopupMVP
        visible={visibleModalBindUnbind}
        onHiding={modalOnHiding}
        title={formatMessage('textButton').bind_unbind}
      >
        <div className="formData">
          <p>{textSO[0]}</p>
          <p>{textSO[1]}</p>
          <div className="formButton">
            <Button
              text={formatMessage('textButton').no}
              type="default"
              stylingMode="text"
              onClick={modalOnHiding}
            />
            <Button
              type="default"
              stylingMode="contained"
              width={260}
              onClick={onClickYes}
            >
              <LoadIndicator className="indicator" visible={loadingRebind} />
              <span>{formatMessage('textButton').yes}</span>
            </Button>
          </div>
        </div>
      </PopupMVP>
    </>
  );
};
export default SalesOrders;
